import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useProfile, moduleEnabled } from "@/context/BusinessProfileContext";
import { api, formatApiError, fileSrc } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import CompanySearchField from "@/components/CompanySearchField";
import { StatusBadge } from "@/components/StatusBadge";
import PhotoUploader from "@/components/PhotoUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { User, ArrowLeft, Car, Plus, Pencil, Trash2, Loader2, Phone, Mail, MapPin, Building2, CalendarDays, Link2 } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "-");
const VEHICLE_EMPTY = (client_id) => ({ client_id, make: "", model: "", version: "", year: "", plate: "", color: "", mileage: "", vehicle_type: "", notes: "", photos: [] });

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { modules } = useProfile();
  const [client, setClient] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [vehOpen, setVehOpen] = useState(false);
  const [vehForm, setVehForm] = useState(VEHICLE_EMPTY(id));
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${id}`);
      setClient(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const genPortal = async () => {
    try {
      const { data } = await api.post(`/clients/${id}/portal-link`);
      const url = `${window.location.origin}/mon-espace?token=${data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Lien de l'espace client copié dans le presse-papier"); }
      catch { toast.success("Lien généré : " + url); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveClient = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${id}`, {
        first_name: editForm.first_name, last_name: editForm.last_name, company: editForm.company,
        siren: editForm.siren, siret: editForm.siret,
        address: editForm.address, phone: editForm.phone, email: editForm.email, notes: editForm.notes,
      });
      toast.success("Client mis à jour");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const saveVehicle = async () => {
    if (!vehForm.make) return toast.error("La marque est requise");
    setSaving(true);
    try {
      await api.post("/vehicles", { ...vehForm, client_id: id });
      toast.success("Véhicule ajouté");
      setVehOpen(false);
      setVehForm(VEHICLE_EMPTY(id));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const deleteClient = async () => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client supprimé");
      navigate("/clients");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!client) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate("/clients")} className="mb-4 text-slate-500 hover:text-slate-900" data-testid="btn-back">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Retour aux clients
      </Button>

      <PageHeader
        title={`${client.first_name || ""} ${client.last_name || ""}`.trim() || client.company || "Client"}
        subtitle={client.company ? `Professionnel · ${client.company}` : "Particulier"}
        icon={User}
        actions={
          <>
            <Button variant="outline" onClick={genPortal} data-testid="btn-client-portal-link" className="border-slate-200 bg-white">
              <Link2 className="w-4 h-4 mr-1.5" /> Espace client
            </Button>
            <Button variant="outline" onClick={() => { setEditForm(client); setEditOpen(true); }} data-testid="btn-edit-client" className="border-slate-200 bg-white">
              <Pencil className="w-4 h-4 mr-1.5" /> Modifier
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="btn-delete-client" className="border-red-500/30 bg-red-500/5 text-red-600 hover:bg-red-500/10 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white border-slate-200">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-slate-900">Supprimer ce client ?</AlertDialogTitle>
                  <AlertDialogDescription>Cette action supprimera aussi ses véhicules. Irréversible.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-slate-200">Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteClient} className="bg-red-500 hover:bg-red-600" data-testid="btn-confirm-delete-client">Supprimer</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          {client.phone && <Info icon={Phone} value={client.phone} />}
          {client.email && <Info icon={Mail} value={client.email} />}
          {client.address && <Info icon={MapPin} value={client.address} />}
          {client.company && <Info icon={Building2} value={client.company} />}
          {client.siren && <Info icon={Building2} value={`SIREN ${client.siren}`} />}
          {client.siret && <Info icon={Building2} value={`SIRET ${client.siret}`} />}
          <Info icon={CalendarDays} value={`Client depuis le ${fmtDate(client.created_at)}`} />
          {client.notes && <div className="text-sm text-slate-700 pt-2 border-t border-slate-200">{client.notes}</div>}
        </div>

        <div className="lg:col-span-2">
          <Tabs defaultValue="vehicles">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="vehicles" data-testid="tab-vehicles">Véhicules ({client.vehicles.length})</TabsTrigger>
              <TabsTrigger value="appointments" data-testid="tab-appointments">Rendez-vous ({client.appointments.length})</TabsTrigger>
              <TabsTrigger value="quotes" data-testid="tab-quotes">Devis ({(client.quotes || []).length})</TabsTrigger>
              <TabsTrigger value="invoices" data-testid="tab-invoices">Factures ({(client.invoices || []).length})</TabsTrigger>
            </TabsList>

            <TabsContent value="vehicles" className="mt-4">
              <div className="flex justify-end mb-3">
                <Button size="sm" onClick={() => setVehOpen(true)} data-testid="btn-add-vehicle" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">
                  <Plus className="w-4 h-4 mr-1.5" /> Ajouter un véhicule
                </Button>
              </div>
              {client.vehicles.length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center">Aucun véhicule.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {client.vehicles.map((v) => (
                    <button key={v.id} onClick={() => navigate(`/vehicles/${v.id}`)} className="text-left bg-slate-50 border border-slate-200 rounded-lg p-4 hover:border-emerald-500/40 transition" data-testid={`vehicle-card-${v.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Car className="w-4 h-4 text-emerald-600" />
                        <span className="font-medium text-slate-900">{v.make} {v.model}</span>
                      </div>
                      <div className="text-xs text-slate-500">{[v.year, v.plate, v.color].filter(Boolean).join(" · ")}</div>
                    </button>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="appointments" className="mt-4">
              {client.appointments.length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center">Aucun rendez-vous.</p>
              ) : (
                <div className="space-y-2">
                  {client.appointments.map((a) => (
                    <div key={a.id} className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-slate-900">{a.title || "Rendez-vous"}</div>
                          <div className="text-xs text-slate-500">{fmtDate(a.start)}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={a.status} />
                          <Link to={`/calendar?appt=${a.id}`} data-testid={`client-appt-open-${a.id}`} className="text-xs text-emerald-700 underline">Ouvrir</Link>
                          {moduleEnabled(modules, "detailing_tools") && <Link to={`/rdv/${a.id}/atelier`} data-testid={`client-appt-atelier-${a.id}`} className="text-xs text-emerald-700 underline">Dossier atelier</Link>}
                        </div>
                      </div>
                      {((a.before_photos || []).length > 0 || (a.after_photos || []).length > 0) && (
                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <div>
                            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Avant ({(a.before_photos || []).length})</div>
                            <div className="flex flex-wrap gap-1.5">
                              {(a.before_photos || []).map((p, i) => (
                                <a key={i} href={fileSrc(p.url || p.path)} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-md overflow-hidden border border-slate-200" data-testid={`appt-${a.id}-before-${i}`}>
                                  <img src={fileSrc(p.url || p.path)} alt="" className="w-full h-full object-cover" />
                                </a>
                              ))}
                              {(a.before_photos || []).length === 0 && <span className="text-[11px] text-slate-400">—</span>}
                            </div>
                          </div>
                          <div>
                            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Après ({(a.after_photos || []).length})</div>
                            <div className="flex flex-wrap gap-1.5">
                              {(a.after_photos || []).map((p, i) => (
                                <a key={i} href={fileSrc(p.url || p.path)} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-md overflow-hidden border border-slate-200" data-testid={`appt-${a.id}-after-${i}`}>
                                  <img src={fileSrc(p.url || p.path)} alt="" className="w-full h-full object-cover" />
                                </a>
                              ))}
                              {(a.after_photos || []).length === 0 && <span className="text-[11px] text-slate-400">—</span>}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="quotes" className="mt-4">
              {(client.quotes || []).length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center">Aucun devis.</p>
              ) : (
                <div className="space-y-2">
                  {client.quotes.map((q) => (
                    <a key={q.id} href={`/quotes`} onClick={(e) => { e.preventDefault(); navigate("/quotes"); }} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-4 hover:border-emerald-500/40" data-testid={`client-quote-${q.id}`}>
                      <div><div className="text-sm font-medium text-slate-900 font-mono">{q.number}</div><div className="text-xs text-slate-500">{q.date}</div></div>
                      <div className="text-sm font-mono text-emerald-600">{(q.total || 0).toFixed(2)} €</div>
                    </a>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="invoices" className="mt-4">
              {(client.invoices || []).length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center">Aucune facture.</p>
              ) : (
                <div className="space-y-2">
                  {client.invoices.map((inv) => (
                    <a key={inv.id} href={`/invoices`} onClick={(e) => { e.preventDefault(); navigate("/invoices"); }} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-4 hover:border-emerald-500/40" data-testid={`client-invoice-${inv.id}`}>
                      <div><div className="text-sm font-medium text-slate-900 font-mono">{inv.number}</div><div className="text-xs text-slate-500">{inv.date} · payé {(inv.amount_paid || 0).toFixed(2)} €</div></div>
                      <div className="text-sm font-mono text-slate-900">{(inv.total || 0).toFixed(2)} €</div>
                    </a>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>      </div>

      {/* Edit client */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader><DialogTitle className="text-slate-900">Modifier le client</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Prénom"><Input value={editForm.first_name || ""} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Nom"><Input value={editForm.last_name || ""} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
            {editForm.client_type === "professionnel" ? (
              <CompanySearchField value={editForm} onChange={(patch) => setEditForm({ ...editForm, ...patch })} testidPrefix="edit-company" />
            ) : (
              <Fld label="Société" full><Input value={editForm.company || ""} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
            )}
            <Fld label="Téléphone"><Input value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Email"><Input value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Adresse" full><Input value={editForm.address || ""} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
            <Fld label="Notes" full><Textarea value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="bg-slate-50 border-slate-200" /></Fld>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button onClick={saveClient} disabled={saving} data-testid="btn-save-client-edit" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add vehicle */}
      <Dialog open={vehOpen} onOpenChange={setVehOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-slate-900">Ajouter un véhicule</DialogTitle></DialogHeader>
          <VehicleFields form={vehForm} setForm={setVehForm} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setVehOpen(false)}>Annuler</Button>
            <Button onClick={saveVehicle} disabled={saving} data-testid="btn-save-vehicle" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ icon: Icon, value }) {
  return <div className="flex items-center gap-2.5 text-sm text-slate-700"><Icon className="w-4 h-4 text-slate-500 shrink-0" />{value}</div>;
}
function Fld({ label, children, full }) {
  return <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}><Label className="text-xs text-slate-500">{label}</Label>{children}</div>;
}

export function VehicleFields({ form, setForm }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid grid-cols-2 gap-3">
      <Fld label="Marque *"><Input data-testid="input-vehicle-make" value={form.make} onChange={set("make")} className="bg-slate-50 border-slate-200" /></Fld>
      <Fld label="Modèle"><Input data-testid="input-vehicle-model" value={form.model} onChange={set("model")} className="bg-slate-50 border-slate-200" /></Fld>
      <Fld label="Version"><Input value={form.version} onChange={set("version")} className="bg-slate-50 border-slate-200" /></Fld>
      <Fld label="Année"><Input value={form.year} onChange={set("year")} className="bg-slate-50 border-slate-200" /></Fld>
      <Fld label="Immatriculation"><Input data-testid="input-vehicle-plate" value={form.plate} onChange={set("plate")} className="bg-slate-50 border-slate-200" /></Fld>
      <Fld label="Couleur"><Input value={form.color} onChange={set("color")} className="bg-slate-50 border-slate-200" /></Fld>
      <Fld label="Kilométrage"><Input value={form.mileage} onChange={set("mileage")} className="bg-slate-50 border-slate-200" /></Fld>
      <Fld label="Type de véhicule"><Input value={form.vehicle_type} onChange={set("vehicle_type")} placeholder="Berline, SUV..." className="bg-slate-50 border-slate-200" /></Fld>
      <Fld label="Remarques" full><Textarea value={form.notes} onChange={set("notes")} className="bg-slate-50 border-slate-200" /></Fld>
      <div className="col-span-2"><PhotoUploader label="Photos du véhicule" value={form.photos} onChange={(photos) => setForm({ ...form, photos })} testid="vehicle-photo" /></div>
    </div>
  );
}
